import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { t } from 'i18next'
import { formatDate } from '../../utility/Helpers'

const ReturnShippingModal = ({
  show,
  onClose,
  onResetPagination,
  shippingList,
  onToggle,
  onPageChange,
  pagination,
  items,
  handleSubmitSearch,
  setKeyword,
  keyword,
  handleReset,
}) => {
  const modalRef = useRef()
  const [expandedId, setExpandedId] = useState(null)

  const displayList = shippingList?.length > 0 ? shippingList : []
  const isAlreadyAdded = (shipCode) => items?.some((item) => item.shipping_code === shipCode)

  const handleClickOutside = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose?.()
      onResetPagination?.()
    }
  }

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose?.()
        onResetPagination?.()
      }
    }

    document.addEventListener('keydown', handleEsc)

    if (show) {
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEsc)
      document.body.style.overflow = 'auto'
    }
  }, [show, onClose, onResetPagination])

  if (!show) return null

  const currentPage = pagination?.page || 1
  const totalPages = pagination?.total_pages || 1

  return (
    <div
      className="modal fade show d-block"
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        inset: 0,
        zIndex: 1050,
        overflowY: 'auto',
      }}
      onClick={handleClickOutside}
    >
      <div className="modal-dialog modal-dialog-centered modal-xl">
        <div className="modal-content" ref={modalRef} style={{ maxWidth: '900px', margin: 'auto' }}>
          <div className="modal-header d-flex justify-content-between align-items-center">
            <h5 className="modal-title fw-bold">{t('title.shipping_selection')}</h5>
            <div className="d-flex align-items-center flex-grow-1 justify-content-end px-3">
              <form onSubmit={handleSubmitSearch} className="w-50">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control rounded-left-pill border-primary"
                    placeholder={t('placeholder.search_keyword')}
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                  />
                  <div className="input-group-append">
                    <button className="btn btn-primary rounded-right-pill" type="submit">
                      <i className="fa fa-search"></i>
                    </button>
                  </div>
                </div>
              </form>
            </div>
            <button className="btn btn-danger btn-sm" onClick={onClose}>
              <i className="fa fa-times"></i>
            </button>
          </div>

          <div
            className="modal-body"
            style={{ maxHeight: '60vh', overflowY: 'auto', backgroundColor: '#f8f9fa' }}
          >
            {displayList.length > 0 ? (
              displayList?.map((item) => (
                <div
                  key={item.shipping_code}
                  className={`card mb-2 border-0 shadow-sm ${
                    isAlreadyAdded(item.shipping_code) ? 'border-left-primary' : ''
                  }`}
                >
                  <div
                    className="card-header bg-white d-flex align-items-center justify-content-between pointer"
                    style={{ cursor: 'pointer', padding: '12px 20px' }}
                    onClick={() =>
                      setExpandedId(expandedId === item.shipping_code ? null : item.shipping_code)
                    }
                  >
                    <div className="d-flex align-items-center">
                      <i
                        className={`fa fa-chevron-${
                          expandedId === item.shipping_code ? 'down' : 'right'
                        } mr-3 text-gray-5`}
                      ></i>
                      <div>
                        <div className="fw-bold text-dark">{item.shipping_code}</div>
                        <small className="text-gray-5">
                          {formatDate(item.shipping_date_delivered, 'YYYY-MM-DD')} |{' '}
                          {item.shipping_number}
                        </small>
                      </div>
                    </div>

                    <div className="d-flex align-items-center">
                      {/* BUTTON TAMBAH DI LEVEL TRANSAKSI */}
                      <button
                        className={`btn btn-sm px-3 ${
                          isAlreadyAdded(item.shipping_code) ? 'btn-danger' : 'btn-primary'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation()
                          onToggle?.(item)
                        }}
                      >
                        {isAlreadyAdded(item.shipping_code)
                          ? t('button.delete')
                          : `+ ${t('button.add')}`}
                      </button>
                    </div>
                  </div>

                  {/* BODY: List Produk */}
                  {expandedId === item.shipping_code && (
                    <div className="card-body p-0 border-top bg-light">
                      <div className="p-2 px-4 bg-white-50 small text-gray-5 font-italic">
                        {t('title.product_list')}:
                      </div>
                      {item.shipping_items?.map((product) => (
                        <div
                          key={product.product_uid}
                          className="d-flex align-items-center p-3 border-bottom mx-3"
                        >
                          <img
                            alt={`${product.product_title}`}
                            src={product.product_thumbnail}
                            width="45"
                            height="45"
                            className="rounded mr-3 border"
                            style={{ objectFit: 'cover', backgroundColor: '#eee' }}
                          />
                          <div className="flex-grow-1">
                            <div className="fw-bold small text-dark">{product.product_title}</div>
                            <div className="text-gray-5" style={{ fontSize: '11px' }}>
                              Qty: {product?.product_quantity} | Rp{' '}
                              {product.product_price_item?.toLocaleString('id-ID')}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="card p-5 text-center text-muted bg-light">
                {t('form.not_found_shipping')}
              </div>
            )}
          </div>

          <div className="modal-footer d-flex justify-content-between">
            <button
              className="btn btn-outline-primary btn-sm"
              disabled={currentPage <= 1}
              onClick={() => onPageChange?.(currentPage - 1)}
            >
              ← {t('title.previous')}
            </button>
            <span className="text-muted small">
              {t('title.page')} <strong>{currentPage}</strong> {t('title.from')}{' '}
              <strong>{totalPages}</strong>
            </span>
            <button
              className="btn btn-outline-primary btn-sm"
              disabled={currentPage >= totalPages}
              onClick={() => onPageChange?.(currentPage + 1)}
            >
              {t('title.next')} →
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

ReturnShippingModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  onResetPagination: PropTypes.func,
  shippingList: PropTypes.array,
  onToggle: PropTypes.func,
  onPageChange: PropTypes.func,
  pagination: PropTypes.shape({
    page: PropTypes.number,
    total_pages: PropTypes.number,
    total_items: PropTypes.number,
    limit: PropTypes.number,
  }),
  items: PropTypes.array,
  handleSubmitSearch: PropTypes.func,
  setKeyword: PropTypes.func,
  keyword: PropTypes.string,
  handleReset: PropTypes.func,
}

export default ReturnShippingModal
